import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

export const ThemeContext = createContext(undefined);

export const FlowstateThemeProvider = ({ children }) => {
    const [mode, setMode] = useState('dark');

    useEffect(() => {
        const savedMode = localStorage.getItem('flowstate_theme');
        if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
            setMode(savedMode);
        } else {
            setMode('dark');
        }
    }, []);

    const toggleTheme = () => {
        setMode((prev) => {
            const newMode = prev === 'light' ? 'dark' : 'light';
            localStorage.setItem('flowstate_theme', newMode);
            return newMode;
        });
    };

    // CSS Variable Theme Injection
    useEffect(() => {
        const root = document.documentElement;
        const colors = mode === 'light' ? {
            '--bg-default': '#f8fafc',
            '--bg-paper': '#ffffff',
            '--bg-glass': 'rgba(255, 255, 255, 0.8)',
            '--bg-sidebar': '#ffffff',
            '--text-primary': '#0f172a',
            '--text-secondary': '#64748b',
            '--border-main': 'rgba(0, 0, 0, 0.06)',
            '--primary-main': '#3b82f6',
            '--accent-glow': 'rgba(59, 130, 246, 0.05)',
            '--shadow-sm': '0 1px 2px rgba(0,0,0,0.05)',
            '--shadow-md': '0 4px 6px -1px rgba(0,0,0,0.1)',
            '--shadow-lg': '0 10px 15px -3px rgba(0,0,0,0.1)',
            '--glass-border': 'rgba(255, 255, 255, 0.5)',
            '--card-glow': 'rgba(59, 130, 246, 0.1)',
            '--scrollbar-thumb': '#cbd5e1',
            '--scrollbar-track': 'transparent'
        } : {
            '--bg-default': '#0a0a0c',
            '--bg-paper': '#18181b',
            '--bg-glass': 'rgba(17, 17, 19, 0.6)',
            '--bg-sidebar': '#09090b',
            '--text-primary': '#f1f5f9',
            '--text-secondary': '#a1a1aa',
            '--border-main': 'rgba(255, 255, 255, 0.06)',
            '--primary-main': '#3b82f6',
            '--accent-glow': 'rgba(59, 130, 246, 0.1)',
            '--shadow-sm': '0 1px 2px rgba(0,0,0,0.3)',
            '--shadow-md': '0 4px 6px -1px rgba(0,0,0,0.5)',
            '--shadow-lg': '0 20px 40px -10px rgba(0,0,0,0.7)',
            '--glass-border': 'rgba(255, 255, 255, 0.05)',
            '--card-glow': 'rgba(59, 130, 246, 0.2)',
            '--scrollbar-thumb': '#52525b',
            '--scrollbar-track': 'rgba(255, 255, 255, 0.02)'
        };

        Object.entries(colors).forEach(([key, value]) => {
            root.style.setProperty(key, value);
        });
    }, [mode]);

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    ...(mode === 'light'
                        ? {
                            background: {
                                default: '#f8fafc',
                                paper: '#ffffff',
                            },
                            primary: {
                                main: '#3b82f6',
                            },
                            text: {
                                primary: '#0f172a',
                                secondary: '#64748b',
                            },
                        }
                        : {
                            background: {
                                default: '#0a0a0c',
                                paper: '#18181b',
                            },
                            primary: {
                                main: '#3b82f6',
                            },
                            text: {
                                primary: '#f1f5f9',
                                secondary: '#a1a1aa',
                            },
                        }),
                },
                typography: {
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    h3: { fontWeight: 900, letterSpacing: '-1px' },
                    h6: { fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' },
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: `
                            * {
                                scrollbar-width: thin;
                                scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
                            }
                            *::-webkit-scrollbar {
                                width: 6px;
                                height: 6px;
                            }
                            *::-webkit-scrollbar-track {
                                background: var(--scrollbar-track);
                            }
                            *::-webkit-scrollbar-thumb {
                                background-color: var(--scrollbar-thumb);
                                border-radius: 20px;
                                border: 1px solid var(--scrollbar-track);
                            }
                            *::-webkit-scrollbar-thumb:hover {
                                background-color: ${mode === 'light' ? '#94a3b8' : '#52525b'};
                            }
                        `,
                    },
                },
            }),
        [mode],
    );

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useThemeContext must be used within a FlowstateThemeProvider');
    }
    return context;
};

export default FlowstateThemeProvider;
